import type { Workspace } from "@/types/workspace";

import { useMockStore } from "../store";
import { generateWorkspaces } from "../generators/workspace";

export function createWorkspaceMock(name: string): Workspace {
  const store = useMockStore.getState();
  if (!store.organization) throw new Error("Organization not found");
  if (!name.trim()) throw new Error("Workspace name is required");
  const workspace = {
    ...generateWorkspaces(store.organization.id, 1)[0],
    name: name.trim(),
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  store.addWorkspace(workspace);
  return workspace;
}

/**
 * Get all workspaces
 */
export function getWorkspacesMock(): Workspace[] {
  const store = useMockStore.getState();

  return store.workspaces;
}

/**
 * Get a single workspace by ID
 */
export function getWorkspaceMock(workspaceId: string): Workspace | undefined {
  const store = useMockStore.getState();

  return store.workspaces.find((w) => w.id === workspaceId);
}

/**
 * Update a workspace
 */
export function updateWorkspaceMock(
  workspaceId: string,
  updates: Partial<Workspace>,
): Workspace | undefined {
  const store = useMockStore.getState();
  const workspace = store.workspaces.find((w) => w.id === workspaceId);

  if (!workspace) return undefined;

  const next: Partial<Workspace> = {
    ...updates,
    config: updates.config
      ? { ...workspace.config, ...updates.config }
      : workspace.config,
  };

  if (updates.workingHours !== undefined) {
    const effectiveFrom = updates.workingHoursEffectiveFrom;

    if (
      updates.workingHours &&
      effectiveFrom &&
      effectiveFrom > new Date().toISOString().slice(0, 10)
    ) {
      next.pendingWorkingHours = { effectiveFrom, hours: updates.workingHours };
      next.workingHours = workspace.workingHours;
      next.workingHoursEffectiveFrom = workspace.workingHoursEffectiveFrom;
    } else {
      next.pendingWorkingHours = null;
    }
  }

  store.updateWorkspace(workspaceId, next);

  return { ...workspace, ...next };
}
